/**
 * CR310: V2 Error Boundary
 * 
 * Safe fallback for malformed V2 specs.
 * Shows admin-only warning UI and renders minimal safe card (no crash).
 */

import React, { Component, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

interface V2ErrorBoundaryProps {
  children: ReactNode;
  dealId?: string;
  dealTypeKey?: string;
  isAdmin?: boolean;
}

interface V2ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class V2ErrorBoundary extends Component<V2ErrorBoundaryProps, V2ErrorBoundaryState> {
  constructor(props: V2ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): V2ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[V2ErrorBoundary] Deal card render error:", {
      dealId: this.props.dealId,
      dealTypeKey: this.props.dealTypeKey,
      error: error.message,
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      const { isAdmin, dealId, dealTypeKey } = this.props;
      
      return (
        <div 
          className={cn(
            "rounded-lg border p-4 min-h-[200px] flex flex-col items-center justify-center gap-2",
            isAdmin 
              ? "bg-destructive/10 border-destructive/30" 
              : "bg-muted/50 border-border"
          )}
        >
          {isAdmin ? (
            <>
              <AlertTriangle className="h-8 w-8 text-destructive" />
              <p className="text-sm font-medium text-destructive">V2 Render Error</p>
              <p className="text-xs text-muted-foreground text-center max-w-[200px]">
                Template malformed for {dealTypeKey || "unknown"} deal
              </p>
              {this.state.error && (
                <code className="text-[10px] text-destructive/70 mt-2 max-w-full overflow-hidden text-ellipsis">
                  {this.state.error.message.slice(0, 100)}
                </code>
              )}
              <p className="text-[10px] text-muted-foreground mt-1">
                ID: {dealId || "N/A"}
              </p>
            </>
          ) : (
            <>
              <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
              <div className="h-4 w-24 bg-muted rounded animate-pulse" />
            </>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-friendly wrapper for functional components
 */
export function withV2ErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  getErrorProps?: (props: P) => { dealId?: string; dealTypeKey?: string; isAdmin?: boolean }
) {
  return function V2ProtectedComponent(props: P) {
    const errorProps = getErrorProps?.(props) ?? {};
    return (
      <V2ErrorBoundary {...errorProps}>
        <WrappedComponent {...props} />
      </V2ErrorBoundary>
    );
  };
}
